import {
  CITE_CHECK_DAILY_LIMIT,
  CITE_CHECK_TEXT_MAX,
  type CiteCheckDocument,
  type CiteCheckItem,
  type CiteCheckQuote,
  type CiteCheckResult,
  type CiteCheckStatus,
} from '@buakei/types';
import { Badge, Button, EmptyState, Notice } from '@buakei/ui';
import { useMutation } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router';
import { Denied, useReaderTokens } from '@/pages/library/LibraryPages';
import { libraryApi } from '@/services/api';
import { Page } from '@/shell/Page';

/**
 * Check citations (ADR-036): paste a draft and see each citation checked against the library,
 * without AI. "Not found" means not found within the library, never that the law or case does
 * not exist. The draft is not stored.
 */

const STATUS: Record<CiteCheckStatus, { label: string; tone: 'success' | 'danger' | 'plain' }> = {
  found: { label: 'Found in the library', tone: 'success' },
  probable: { label: 'Close match: check it', tone: 'plain' },
  mismatch: { label: 'Citation and names do not agree', tone: 'danger' },
  not_found: { label: 'Not found within the library', tone: 'danger' },
  not_checked: { label: 'Not checked', tone: 'plain' },
};
const KIND: Record<CiteCheckItem['kind'], string> = {
  case: 'Case',
  act: 'Act',
  section: 'Section',
  law_report: 'Law report',
};
/** Problems first, so they are seen before the citations that check out. */
const ORDER: CiteCheckStatus[] = ['mismatch', 'not_found', 'probable', 'not_checked', 'found'];

function quoteLabel(q: CiteCheckQuote): string {
  const page = q.pageStart ? ` (page ${q.pageStart})` : '';
  switch (q.status) {
    case 'matches':
      return `The quotation matches the text${page}.`;
    case 'likely':
      return `Most of the wording appears${page}, but the extracted text is out of order there. Check the page.`;
    case 'differs':
      return `The wording differs from the text${page}. Check the quotation.`;
    case 'not_found':
      return "The wording was not found in the document's extracted text. Check the quotation against the original.";
  }
}

function docLink(d: CiteCheckDocument, page?: number | null) {
  const path = `/library/${d.collection === 'cases' ? 'cases/' : ''}${d.versionId}`;
  return page ? `${path}?page=${page}` : path;
}

function DocLink({ d, page }: { d: CiteCheckDocument; page?: number | null }) {
  return (
    <Link
      to={docLink(d, page)}
      className="font-medium text-navy-900 underline-offset-2 hover:underline"
    >
      {d.title}
      {d.year && !d.title.includes(String(d.year)) ? ` (${d.year})` : ''}
    </Link>
  );
}

function ItemCard({ item }: { item: CiteCheckItem }) {
  const status = STATUS[item.status];
  return (
    <li className="flex flex-col gap-2 p-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-xs font-semibold tracking-wide text-muted-600 uppercase">
          {KIND[item.kind]}
        </span>
        <span className="font-serif text-base font-semibold text-navy-900">{item.cited}</span>
        {item.occurrences > 1 ? (
          <span className="text-xs text-muted-600">cited {item.occurrences} times</span>
        ) : null}
        <Badge tone={status.tone}>{status.label}</Badge>
      </div>
      {item.match ? (
        <p className="text-sm text-ink-900">
          In the library: <DocLink d={item.match} page={item.section?.pageStart} />
          {item.citedBy ? (
            <span className="text-muted-600">
              {' '}
              · named in {item.citedBy} later {item.citedBy === 1 ? 'judgment' : 'judgments'} in the
              library: check how they treated it
            </span>
          ) : null}
        </p>
      ) : null}
      {item.candidates.length > 0 ? (
        <div className="text-sm text-ink-900">
          {item.status === 'mismatch' ? 'This citation in the library: ' : 'Possible matches: '}
          <ul className="mt-1 flex flex-col gap-1 pl-4">
            {item.candidates.map((c) => (
              <li key={c.versionId} className="list-disc">
                <DocLink d={c} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {item.section?.excerpt ? (
        <blockquote className="border-l-2 border-navy-900 pl-3 font-serif text-sm text-ink-900">
          {item.section.excerpt}
        </blockquote>
      ) : null}
      {item.quotes.map((q) => (
        <div key={q.text} className="flex flex-col gap-1 rounded-md bg-navy-50 px-3 py-2 text-sm">
          <span className="font-serif text-ink-900">“{q.text}”</span>
          <span className={q.status === 'matches' ? 'text-ok-700' : 'text-ink-900'}>
            {quoteLabel(q)}
          </span>
          {q.closest ? (
            <span className="text-muted-600">Closest text in the document: {q.closest}</span>
          ) : null}
        </div>
      ))}
      {item.laterActs.length > 0 ? (
        <div className="text-sm text-ink-900">
          Later Acts of the same name in the library. Check whether they amend or repeal it:
          <ul className="mt-1 flex flex-col gap-1 pl-4">
            {item.laterActs.map((a) => (
              <li key={a.versionId} className="list-disc">
                <DocLink d={a} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {item.note ? <p className="text-sm text-muted-600">{item.note}</p> : null}
    </li>
  );
}

function Results({ result }: { result: CiteCheckResult }) {
  if (result.items.length === 0) {
    return (
      <EmptyState title="No citations found in the text">
        Citations are recognised in these forms: a neutral citation such as [1981] SLSC 1, an Act by
        name and year such as the Companies Act 2009, and sections of those Acts. Nothing was
        counted.
      </EmptyState>
    );
  }
  const items = [...result.items].sort(
    (a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status) || a.id - b.id,
  );
  return (
    <section aria-labelledby="results-h" className="flex flex-col gap-3">
      <h2 id="results-h" className="font-serif text-xl font-semibold text-navy-900">
        {result.items.length} {result.items.length === 1 ? 'citation' : 'citations'} checked
      </h2>
      <p role="status" className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-900">
        {ORDER.filter((s) => result.counts[s]).map((s) => (
          <span key={s}>
            {STATUS[s].label}: <strong className="tabular-nums">{result.counts[s]}</strong>
          </span>
        ))}
      </p>
      <p className="text-sm text-muted-600">
        Checked against {result.library.acts} Acts and {result.library.cases} judgments in the
        library, whose text was extracted automatically and has not been verified. A citation found
        here still needs reading: this checks that it exists and is quoted correctly, not that it
        supports your argument or is still good law. {result.remainingToday} of{' '}
        {CITE_CHECK_DAILY_LIMIT} checks left today.
      </p>
      <ul
        aria-label="Citations"
        className="flex flex-col divide-y divide-line-200 rounded-lg border border-line-200 bg-white"
      >
        {items.map((item) => (
          <ItemCard key={item.id} item={item} />
        ))}
      </ul>
    </section>
  );
}

export function CiteCheckPage() {
  const tokens = useReaderTokens();
  const id = useId();
  const [text, setText] = useState('');
  const check = useMutation({ mutationFn: () => libraryApi.citeCheck(tokens!, text) });
  useEffect(() => {
    document.title = 'Check citations · Buakei AI';
  }, []);
  if (!tokens) return <Denied />;
  const tooShort = text.trim().length < 10;
  return (
    <Page
      title="Check citations"
      description="Paste a draft, such as submissions, an opinion or an essay, to check each case and Act it cites against the library."
      width="wide"
    >
      <Notice title="Your text stays private" icon={ShieldCheck}>
        The text is checked on our server and is not stored or sent to an AI model. Only a count of
        today's checks is kept. Remove client names if you can.
      </Notice>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!tooShort) check.mutate();
        }}
      >
        <label htmlFor={id} className="text-sm font-medium text-ink-900">
          Your draft
        </label>
        <textarea
          id={id}
          rows={12}
          maxLength={CITE_CHECK_TEXT_MAX}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="For example: In Seymour-Wilson v Musa Abess [1981] SLSC 1 the Supreme Court held… Under section 26 of the Companies Act 2009…"
          className="rounded-md border border-line-200 bg-white p-3 text-ink-900 placeholder:text-muted-600"
        />
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={tooShort || check.isPending}>
            {check.isPending ? 'Checking…' : 'Check citations'}
          </Button>
          <span className="text-sm text-muted-600 tabular-nums">
            {text.length.toLocaleString('en-GB')} of {CITE_CHECK_TEXT_MAX.toLocaleString('en-GB')}{' '}
            characters
          </span>
        </div>
      </form>
      {check.isError ? (
        <p role="alert" className="text-sm text-ink-900">
          {check.error.message}
        </p>
      ) : null}
      {check.data ? <Results result={check.data} /> : null}
    </Page>
  );
}
